import {
  Habit,
  QueryFilterOptionKeys,
  RepeatingTask,
  TaskFilterOptions,
  TaskPriorityOptions,
  Timestamp,
  WeekDays,
} from '@griply/types';

import { cleanDeep } from './clean-deep';
import { getDueTaskPlanning, getTaskPlanning } from './get-task-planning';
import {
  isDurationFilterActive,
  matchesDurationFilter,
} from './matches-duration-filter';

export type ProcessScheduleTasksOptions = {
  weekStartsOn: WeekDays;
  today: Timestamp;
};

export const processScheduleTasks = (
  tasks: Habit[] | RepeatingTask[],
  filters: TaskFilterOptions | TaskFilterOptions[],
  { weekStartsOn, today }: ProcessScheduleTasksOptions,
) => {
  const filtersArray = Array.isArray(filters) ? filters : [filters];

  // Optimization: Clean filters once
  const processedFiltersArray = filtersArray.map((f) => cleanDeep(f));

  // If any filter set is empty, it means "no restrictions" for that set, so it matches everything.
  const hasEmptyFilter = processedFiltersArray.some(
    (f) => !Object.values(f).length,
  );

  if (hasEmptyFilter) {
    return tasks;
  }

  // process every filter
  return tasks.filter((task) =>
    // Check if task matches ANY of the filter sets
    processedFiltersArray.some((processedFilters, index) => {
      if (
        (processedFilters.deleted && !task.deletedAt) ||
        (!processedFilters.deleted && !!task.deletedAt)
      ) {
        return false;
      }
      const originalFilter = filtersArray[index];
      // filtering on ids without any ids present means no tasks
      if (originalFilter.ids && !originalFilter.ids.length) {
        return false;
      }

      if (
        processedFilters.deadlineDeadlineStartDate ||
        processedFilters.deadlineDeadlineEndDate ||
        processedFilters.hasDeadlineDeadline ||
        processedFilters.noDeadline
      ) {
        return false;
      }

      if (
        processedFilters.priorities?.length &&
        !(
          !task.priority &&
          processedFilters.priorities.includes(TaskPriorityOptions.None)
        ) &&
        !processedFilters.priorities.includes(task.priority!)
      ) {
        return false;
      }

      if (
        processedFilters.ids?.length &&
        !processedFilters.ids.includes(task.id)
      ) {
        return false;
      }

      if (
        processedFilters.excludeIds?.length &&
        processedFilters.excludeIds.includes(task.id)
      ) {
        return false;
      }

      if (processedFilters.noLifeArea && task.lifeAreaId) {
        return false;
      }

      if (
        processedFilters.noLifeArea &&
        task.goalId &&
        processedFilters.lifeAreaLinkedGoalIds?.includes(task.goalId)
      ) {
        return false;
      }

      if (processedFilters.noGoal && task.goalId) {
        return false;
      }

      if (
        processedFilters.archivedGoalIds?.length &&
        task.goalId &&
        processedFilters.archivedGoalIds.includes(task.goalId)
      ) {
        return false;
      }

      if (!processedFilters.includeArchived && task.archivedAt) {
        return false;
      }

      const linkedItemIds =
        processedFilters.lifeAreas?.length || processedFilters.goals?.length
          ? [
              ...(processedFilters.lifeAreas ?? []),
              ...(processedFilters.goals ?? []),
            ]
          : undefined;

      if (
        linkedItemIds &&
        !(
          (task.lifeAreaId && linkedItemIds.includes(task.lifeAreaId)) ||
          (task.goalId && linkedItemIds.includes(task.goalId))
        )
      ) {
        return false;
      }

      const shouldCheckPlanning =
        processedFilters[QueryFilterOptionKeys.DeadlineStartDate] ||
        processedFilters[QueryFilterOptionKeys.DeadlineEndDate];

      // A recurring item done only for today keeps recurring — it stays visible
      // at its next occurrence. Only a fully-completed (`completedAt`) task/series
      // is hidden here, matching one-off tasks.
      if (
        !shouldCheckPlanning &&
        !processedFilters.showCompleted &&
        task.completedAt
      ) {
        return false;
      }

      if (!shouldCheckPlanning) {
        if (!isDurationFilterActive(processedFilters)) {
          return true;
        }
        // Filter on the SHOWN occurrence's timeslot — what the single-occurrence
        // views (today / all-tasks / detail) render — resolved via the same
        // planning the builders use, so the filter matches the card.
        const shownOccurrence = getDueTaskPlanning(task, {
          date: today,
          uncompletedOnly: !processedFilters.showCompletedForDate,
          weekStartsOn,
          today,
        })[0];
        return matchesDurationFilter(
          shownOccurrence?.timeslot?.duration,
          processedFilters,
        );
      }

      const deadlineStartDate =
        processedFilters[QueryFilterOptionKeys.DeadlineStartDate];
      const deadlineEndDate =
        processedFilters[QueryFilterOptionKeys.DeadlineEndDate];

      // Calendar-style consumers (showCompletedForDate) keep tasks whose
      // in-window occurrences are already completed — visibility of those
      // occurrences is decided downstream, not by the sourcing filter.
      const uncompletedOnly = !processedFilters.showCompletedForDate;

      const planning =
        deadlineStartDate && deadlineEndDate
          ? getTaskPlanning(task, {
              startDate: deadlineStartDate as Timestamp,
              endDate: deadlineEndDate as Timestamp,
              today,
              weekStartsOn,
              uncompletedOnly,
            })
          : getDueTaskPlanning(task, {
              date: (deadlineStartDate ??
                deadlineEndDate ??
                today) as Timestamp,
              uncompletedOnly,
              weekStartsOn,
              today,
            });

      // A "times per …" (frequency) schedule only ever surfaces an occurrence in
      // the timeframe containing `today`. An end-only window whose end is in the
      // future (Upcoming: end = maxDate) probes a point where the task is never
      // due, so it drops out. Re-probe at `today`; the `> deadlineEndDate` guard
      // below still bounds past-end windows.
      const finalPlanning =
        !planning.length && !deadlineStartDate && deadlineEndDate
          ? getDueTaskPlanning(task, {
              date: today,
              uncompletedOnly,
              weekStartsOn,
              today,
            })
          : planning;

      if (!finalPlanning.length) {
        return false;
      }

      const plannedOccurrence = finalPlanning[0];

      if (deadlineStartDate && plannedOccurrence.date < deadlineStartDate) {
        return false;
      }
      if (deadlineEndDate && plannedOccurrence.date > deadlineEndDate) {
        return false;
      }

      // Multi-occurrence views (upcoming / calendar) expand this task into many
      // occurrences and filter each one by its own timeslot downstream. Here we
      // only keep the task when AT LEAST ONE in-window occurrence matches, so a
      // non-matching first occurrence can't hide a later matching one.
      if (
        isDurationFilterActive(processedFilters) &&
        !finalPlanning.some((occurrence) =>
          matchesDurationFilter(
            occurrence.timeslot?.duration,
            processedFilters,
          ),
        )
      ) {
        return false;
      }

      if (task.completedAt) {
        if (
          processedFilters[QueryFilterOptionKeys.CompletedStartDate] &&
          task.completedAt <
            processedFilters[QueryFilterOptionKeys.CompletedStartDate]
        ) {
          return false;
        }
        if (
          processedFilters[QueryFilterOptionKeys.CompletedEndDate] &&
          task.completedAt >
            processedFilters[QueryFilterOptionKeys.CompletedEndDate]
        ) {
          return false;
        }
      }

      return true;
    }),
  );
};
